import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import { isMethodAvailable } from '../../utils';
import { FlowTypes } from '../constants';
import type { UIPaymentGroups, UIPayoutGroups } from '../formatters';
import { sortByArrangement } from '../formatters';
import type { RootState } from '../index';

interface CategorySelection {
  category_name: string;
  payGroupId: number;
}

interface MethodGroupingState {
  /** is_in_carousel from merchant_paygroup_category */
  isShowGroupingMethodsInCarousel: boolean;
  /** Currently selected category tab */
  selectedCategory: string | null;
  /** User-selected method per category (persisted during session) */
  userSelections: CategorySelection[];
  /** Previous selected category (for change detection in auto-select) */
  prevSelectedCategory: string | null;
}

const initialState: MethodGroupingState = {
  isShowGroupingMethodsInCarousel: false,
  selectedCategory: null,
  userSelections: [],
  prevSelectedCategory: null,
};

const methodGroupingSlice = createSlice({
  name: 'methodGrouping',
  initialState,
  reducers: {
    setShowGroupingMethodsInCarousel: (state, action: PayloadAction<boolean>) => {
      state.isShowGroupingMethodsInCarousel = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategory = action.payload;
    },
    setUserCategorySelection: (state, action: PayloadAction<CategorySelection>) => {
      const { category_name, payGroupId } = action.payload;
      const existingSelection = state.userSelections.find((s) => s.category_name === category_name);

      if (existingSelection) {
        existingSelection.payGroupId = payGroupId;
      } else {
        state.userSelections.push(action.payload);
      }
    },
    resetCategoryState: (state) => {
      state.selectedCategory = null;
      state.userSelections = [];
      state.prevSelectedCategory = null;
    },
    setPrevSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.prevSelectedCategory = action.payload;
    },
    resetCategorySelection: (state, action: PayloadAction<string>) => {
      state.userSelections = state.userSelections.filter((s) => s.category_name !== action.payload);
    },
  },
});

export const {
  setShowGroupingMethodsInCarousel,
  setSelectedCategory,
  setUserCategorySelection,
  resetCategoryState,
  setPrevSelectedCategory,
  resetCategorySelection,
} = methodGroupingSlice.actions;

export default methodGroupingSlice.reducer;

// Selectors
export const isShowGroupingMethodsInCarouselSelector = (state: RootState) =>
  state.methodGrouping.isShowGroupingMethodsInCarousel;
export const selectedCategorySelector = (state: RootState) => state.methodGrouping.selectedCategory;
export const userSelectionsSelector = (state: RootState) => state.methodGrouping.userSelections;
export const prevSelectedCategorySelector = (state: RootState) => state.methodGrouping.prevSelectedCategory;

// ============= DERIVED SELECTORS =============

type Method = UIPaymentGroups | UIPayoutGroups;

const getMethodsForFlow = (state: RootState): Method[] => {
  const flowType = state.common.paymentFlow;
  return flowType === FlowTypes.deposit
    ? ((state.deposit?.depositMethods || []) as Method[])
    : ((state.withdrawal?.withdrawalMethods || []) as Method[]);
};

const getCategoriesForFlow = (state: RootState) => {
  const flowType = state.common.paymentFlow;
  const paygroupCategories =
    flowType === FlowTypes.deposit ? state.deposit?.paygroupCategories : state.withdrawal?.paygroupCategories;
  return paygroupCategories?.categories || [];
};

/**
 * Derived selector: carousel methods for the current category.
 * Replaces dispatch(setCarouselMethods) for grouping mode.
 * Returns methods filtered by selectedCategory, sorted by arrangement (desc).
 */
export const groupingCarouselMethodsSelector = createSelector(
  [selectedCategorySelector, getMethodsForFlow, getCategoriesForFlow],
  (selectedCategory, methods, categories): Method[] => {
    if (!selectedCategory) {
      return methods.filter((m) => isMethodAvailable(m));
    }

    const categoryConfig = categories.find((cat) => cat.category_name === selectedCategory);
    if (!categoryConfig) return [];

    return methods
      .filter((m) => categoryConfig.pay_group_ids.includes(m.id) && isMethodAvailable(m))
      .sort(sortByArrangement);
  },
);
